/// Storage abstraction so tests can pass a fake. In the browser we
/// use `localStorage`; in Capacitor's WebView the same API works and
/// is persisted by the platform.
///
/// All keys are namespaced under `nav-games.auth.*` to avoid colliding
/// with anything else the app stores locally.

import type { StoredSession } from "./types";

export interface KVStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const KEY = "nav-games.auth.session";

export class SessionStorage {
  constructor(private readonly kv: KVStore) {}

  load(): StoredSession | null {
    const raw = this.kv.getItem(KEY);
    if (raw === null) return null;
    try {
      const parsed = JSON.parse(raw) as StoredSession;
      // Light shape check — guard against a stale schema written by
      // an older version of the app.
      if (
        typeof parsed.accessToken !== "string" ||
        typeof parsed.refreshToken !== "string" ||
        typeof parsed.accessExpiresAt !== "number" ||
        typeof parsed.audience !== "string"
      ) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  save(session: StoredSession): void {
    this.kv.setItem(KEY, JSON.stringify(session));
  }

  clear(): void {
    this.kv.removeItem(KEY);
  }
}

/// Convenience: build a SessionStorage backed by `window.localStorage`.
/// Returns `null` if localStorage isn't available (e.g. SSR, privacy
/// mode in older browsers).
export function defaultStorage(): SessionStorage | null {
  if (typeof globalThis.localStorage === "undefined") return null;
  return new SessionStorage(globalThis.localStorage);
}
